'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Link } from '../../Link/Link';
import { useMagazinePublishedDate } from '../hooks/useMagazinePublishedDate';

type ArticlePreviewCardProps = {
  title: string;
  href: string;
  publishedDate: string;
  author: string;
  authorHref?: string;
  imageSrc: string;
  imageAlt?: string;
  category?: string;
  position?: number;
  priority?: boolean;
  'data-testid'?: string;
};

function ArticlePreviewCard({
  title,
  href,
  publishedDate,
  author,
  authorHref,
  imageSrc,
  imageAlt = '',
  category,
  position,
  priority = false,
  'data-testid': dataTestId = 'article-preview-card',
}: ArticlePreviewCardProps) {
  const t = useTranslations('magazine.article_preview_card');
  const formattedDate = useMagazinePublishedDate(publishedDate);

  return (
    <article
      className="flex h-full w-full min-w-[136px] max-w-[248px] flex-col gap-4"
      data-testid={dataTestId}
      data-track-container="Article Preview"
      data-track-title={title}
      data-track-position={position}
      data-track-provider={author}
      data-track-type={category}
    >
      <Link href={href} simplified className="block w-full" aria-label={title} data-testid={`${dataTestId}-image-link`}>
        <Image src={imageSrc} alt={imageAlt} width={660} height={200} className="h-[100px] w-full rounded object-cover" priority={priority} data-testid={`${dataTestId}-image`} />
      </Link>
      <div className="flex min-w-0 flex-col gap-2 break-words">
        <time className="paragraph-s text-foreground" dateTime={publishedDate} data-testid={`${dataTestId}-date`}>
          {formattedDate}
        </time>
        <h2 className="min-w-0 break-words text-foreground">
          {/* Override Link's default inline-flex/items-center so short titles stay top-aligned with wrapped ones. */}
          <Link
            href={href}
            simplified
            className="block w-full items-start justify-start break-words whitespace-normal paragraph-strong hover:underline"
            data-testid={`${dataTestId}-title-link`}
          >
            {title}
          </Link>
        </h2>
      </div>
      {/*TODO determine if Figma's usage of Daxline Pro is accurate for this*/}
      <address className="not-italic break-words paragraph-s text-foreground" data-testid={`${dataTestId}-author`}>
        {t.rich('by', {
          author,
          link: chunks =>
            authorHref ? (
              <Link href={authorHref} simplified size="s" className="inline break-words whitespace-normal" data-testid={`${dataTestId}-author-link`}>
                {chunks}
              </Link>
            ) : (
              <span className="paragraph-s-strong">{chunks}</span>
            ),
        })}
      </address>
    </article>
  );
}

export { ArticlePreviewCard, type ArticlePreviewCardProps };
